export const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function apiUrl(path = '') {
  return `${API_URL}${path}`;
}

// ── Détection automatique d'un redémarrage de service (2026-05-21) ───────────
// Le portail est redéployé fréquemment, ce qui produit ~30-60s pendant lesquels
// les requêtes API renvoient 502/503/504 ou un network error. Sans signal
// visuel, les users croient à un bug et ouvrent des tickets pour rien.
//
// Mécanisme : compteur d'erreurs consécutives → si ≥ 2 erreurs dans 30s,
// on dispatch un CustomEvent('api-status-change', {detail:{status:'maintenance'}}).
// Quand une requête réussit après ça, on dispatch 'online'.
// Le composant MaintenanceBanner écoute cet event et affiche/cache le bandeau.
let _apiErrorCount = 0;
let _apiLastErrorAt = 0;
let _apiInMaintenance = false;
const MAINTENANCE_THRESHOLD = 2;        // nb d'erreurs consécutives avant bascule
const MAINTENANCE_WINDOW_MS = 30_000;   // dans cette fenêtre

function _emitApiStatus(status) {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('api-status-change', { detail: { status } }));
  } catch {}
}

function _recordApiError() {
  const now = Date.now();
  if (now - _apiLastErrorAt < MAINTENANCE_WINDOW_MS) {
    _apiErrorCount += 1;
  } else {
    _apiErrorCount = 1;
  }
  _apiLastErrorAt = now;
  if (_apiErrorCount >= MAINTENANCE_THRESHOLD && !_apiInMaintenance) {
    _apiInMaintenance = true;
    _emitApiStatus('maintenance');
  }
}

function _recordApiSuccess() {
  if (_apiInMaintenance) {
    _apiInMaintenance = false;
    _emitApiStatus('online');
  }
  _apiErrorCount = 0;
}

// Lien magique d'intervention RETIRÉ (15/10/2026, #20914) : l'en-tête X-Intervention-Token n'est plus envoyé, et
// les clés que l'ancienne page déposait (aucun code ne les effaçait) sont retirées une fois, au chargement.
const CLES_ANCIEN_LIEN = ['meteris_intervention_token', 'meteris_intervention_context',
  'meteris_intervention_expires_at', 'meteris_intervention_mode'];
try {
  if (typeof sessionStorage !== 'undefined') CLES_ANCIEN_LIEN.forEach((cle) => sessionStorage.removeItem(cle));
} catch {}

export async function apiFetch(path, options = {}) {
  const { headers, ...rest } = options;
  // Auto-inject X-Active-Org header from localStorage (set by OrgSelector)
  // Value is the internal org code (METERIS/MCF/SCI), not a client UUID
  const orgHeaders = {};
  try {
    let activeOrg = typeof localStorage !== 'undefined' ? localStorage.getItem('tk_selected_org') : null;
    // Normalize legacy lowercase values
    if (activeOrg === 'meteris') activeOrg = 'METERIS';
    else if (activeOrg === 'mcf') activeOrg = 'MCF';
    else if (activeOrg === 'sci-les-tilleuls' || activeOrg === 'sci') activeOrg = 'SCI';
    if (activeOrg && activeOrg !== 'all' && activeOrg !== 'ALL') {
      orgHeaders['X-Active-Org'] = activeOrg;
    }
  } catch {}
  // Declarer le type REEL du corps. Sans cet en-tete, le navigateur envoie un
  // JSON en `text/plain;charset=UTF-8`. La plupart des routes lisent
  // `await request.json()` et s'en moquent -- le defaut restait donc invisible --
  // mais une route qui lie un modele Pydantic (`body: TaskCreate`) refuse un
  // corps `text/plain` avec un 422. Mesure du 20/09/2026 : 24 appels concernes,
  // dont QuickTaskModal, reellement casse. Constat 138 du registre #20266.
  //
  // Trois garde-fous : on ne touche qu'a un corps CHAINE (un FormData doit
  // laisser le navigateur poser son `boundary` multipart, sinon tous les envois
  // de fichiers cassent), et jamais si l'appelant a deja pose l'en-tete, quelle
  // qu'en soit la casse.
  const typeHeaders = {};
  const entetesFournis = headers || {};
  // Écran d'où part la requête : le serveur le recopie sur les lignes d'Historique des tickets
  // (core/origine_requete.py, ticket #21056 : « il faut voir d'où vient l'appel »). Un appelant
  // peut le préciser ; un chemin non ASCII n'est jamais envoyé (fetch refuserait la requête).
  const origineHeaders = {};
  try {
    const ecran = typeof window !== 'undefined' && window.location ? window.location.pathname : '';
    const dejaPose = Object.keys(entetesFournis).some((k) => k.toLowerCase() === 'x-origine-ecran');
    if (ecran && !dejaPose && /^[\x21-\x7e]+$/.test(ecran)) origineHeaders['X-Origine-Ecran'] = ecran.slice(0, 120);
  } catch {}
  const aDejaLeType = Object.keys(entetesFournis).some((k) => k.toLowerCase() === 'content-type');
  if (typeof rest.body === 'string' && !aDejaLeType) {
    typeHeaders['Content-Type'] = 'application/json';
  }
  try {
    const response = await fetch(apiUrl(path), {
      credentials: 'include',
      headers: { ...orgHeaders, ...typeHeaders, ...origineHeaders, ...headers },
      ...rest,
    });
    // Verrou saisie quotidienne : 423 → événement global (le <DailyLogGuard> écoute)
    if (response.status === 423) {
      try {
        const body = await response.clone().json();
        if (body && body.error_code === 'daily_log_locked' && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('daily-log-locked', {
            detail: { reference_date: body.reference_date, message: body.message },
          }));
        }
      } catch (_) { /* ignore parse error */ }
    }
    // 5xx = service en panne (potentiellement redémarrage en cours)
    if (response.status >= 500 && response.status < 600) {
      _recordApiError();
    } else {
      _recordApiSuccess();
    }
    // Session expirée : le cookie a vécu (JWT_EXPIRE_HOURS), mais l'état
    // isAuthenticated du frontend datait du chargement — chaque page affichait
    // des erreurs jusqu'à F5. L'AuthProvider écoute cet événement et rejoue
    // /api/auth/me : user=null → ProtectedRoute renvoie vers /login?returnTo.
    // Les routes /api/auth/* sont exclues (login raté, /me lui-même).
    if (response.status === 401 && typeof window !== 'undefined' && !String(path).startsWith('/api/auth/')) {
      try { window.dispatchEvent(new CustomEvent('api-unauthorized', { detail: { path } })); } catch (_) { /* ignore */ }
    }
    return response;
  } catch (error) {
    // Une requête annulée par l'appelant (AbortController : recherche
    // débouncée, page qui change) n'est pas une panne : ne pas armer le
    // bandeau maintenance dessus.
    if (error && error.name === 'AbortError') throw error;
    // Network error (Failed to fetch) — typique pendant un restart du container
    _recordApiError();
    throw error;
  }
}

export async function readApiJson(response) {
  const raw = await response.text();
  const trimmed = raw.trim();
  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  const looksLikeJson = contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[');

  if (!looksLikeJson) {
    if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html') || trimmed.startsWith('<')) {
      throw new Error("L'API a renvoyé une page HTML au lieu du JSON attendu. Vérifiez que le backend et le proxy ont bien été redéployés.");
    }
    throw new Error('Réponse API invalide.');
  }

  try {
    return trimmed ? JSON.parse(trimmed) : {};
  } catch {
    throw new Error('Réponse JSON invalide.');
  }
}

// ── okOrThrow : readApiJson + refus des 4xx/5xx et des {ok:false} ───────────
// readApiJson ne lève JAMAIS sur un 4xx JSON : `await apiFetch(...).then(readApiJson);
// toast.success(...)` annonçait un succès sur un 403, un 404 ou un 422 (audit
// 05/09/2026 : 20 sites dans le parc et le ticketing). Ce helper lit la
// réponse et lève avec le message du serveur (error/detail) quand la requête a
// échoué — le `catch` existant du site affiche alors la vraie erreur.
export async function okOrThrow(response) {
  const data = await readApiJson(response);
  if (!response.ok || (data && data.ok === false)) {
    const msg = (data && (data.error || data.detail || data.message)) || `HTTP ${response.status}`;
    const err = new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
}

// ── Bug portail #7 (2026-05-22) : formatter d'erreurs HTTP unifie ───────────
// Avant ce helper, chaque lib/*Api.js refaisait son propre fallback
// `await resp.clone().json()` / `await resp.text()` → quand le backend retombait
// pendant un redeploy, Cloudflare retournait sa page HTML "502 Bad gateway" et
// le toast utilisateur affichait litteralement `HTTP 502 : <!DOCTYPE html>...`.
// Ce formatter detecte ce cas (et les redirections HTML), arme immediatement le
// bandeau maintenance (sans attendre 2 erreurs consecutives), et renvoie un
// message court et actionnable. Le code appelant fait `throw await
// formatApiError(resp)` au lieu de bricoler son propre fallback.
export async function formatApiError(response) {
  const status = response?.status || 0;
  let raw = '';
  try { raw = await response.clone().text(); } catch { raw = ''; }
  const trimmed = (raw || '').trim();
  const looksHtml = trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html') || trimmed.startsWith('<');

  let message = '';
  if (looksHtml) {
    // 5xx + HTML = Cloudflare / proxy entre le client et l'origine. Signal tres
    // fiable que le backend est injoignable (deploy en cours, restart, OOM).
    if (status >= 502 && status <= 504) {
      message = `Service portail temporairement indisponible (HTTP ${status}). Probable redeploiement en cours — reessaye dans quelques secondes.`;
    } else if (status === 0 || status >= 500) {
      message = `Reponse inattendue du proxy (HTTP ${status || '?'}). Le service est probablement en cours de redemarrage.`;
    } else {
      message = `Reponse HTML inattendue (HTTP ${status}). Authentification expiree ou route incorrecte.`;
    }
    // Arme le bandeau maintenance immediatement sur 1 seul signal HTML 5xx
    // (au lieu d'attendre le seuil de 2 erreurs en 30s). HTML upstream = service
    // down certain, inutile de temporiser le feedback visuel global.
    if (status >= 500) {
      _apiErrorCount = MAINTENANCE_THRESHOLD;
      _apiLastErrorAt = Date.now();
      if (!_apiInMaintenance) {
        _apiInMaintenance = true;
        _emitApiStatus('maintenance');
      }
    }
  } else {
    // Tentative JSON FastAPI { detail: ... } / { message: ... }
    try {
      const body = trimmed ? JSON.parse(trimmed) : null;
      const d = body && (body.detail !== undefined ? body.detail : body.message);
      if (Array.isArray(d)) {
        message = d.map((e) => {
          if (typeof e === 'string') return e;
          const loc = Array.isArray(e?.loc) ? e.loc.slice(-2).join('.') : '';
          const msg = e?.msg || e?.type || JSON.stringify(e);
          return loc ? `${loc} : ${msg}` : msg;
        }).join(' ; ');
      } else if (typeof d === 'string') {
        message = d;
      } else if (d) {
        message = JSON.stringify(d);
      } else if (trimmed) {
        message = trimmed.substring(0, 200);
      }
    } catch {
      message = trimmed.substring(0, 200);
    }
  }

  if (!message) {
    message = response?.statusText || `Erreur HTTP ${status || '?'}`;
  }
  const err = new Error(`HTTP ${status} : ${message}`);
  err.status = status;
  err.isUpstreamUnavailable = looksHtml && status >= 500;
  err.rawDetail = message;
  return err;
}
